// In src/store/chartSlice.ts
import { StateCreator } from 'zustand';
// Potrebbe essere necessario importare tipi da ./types se GeoDataState è lì
// import type { GeoDataState } from './types'; // Esempio

// Proprietà specifiche del contenitore del grafico Recharts
export interface ChartContainerProps {
  width?: number;
  height?: number;
  data?: any[]; // Dati per il grafico
  margin?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
    [key: string]: any; // Per altre proprietà di margin se necessario
  };
  layout?: 'horizontal' | 'vertical'; // Per BarChart, ComposedChart etc.
  syncId?: string;
  // Aggiungere qui altre props comuni del contenitore se necessario
  [key: string]: any; // Per flessibilità con altre props specifiche del contenitore
}

// Definizione per i child components
export interface ChartChildComponentConfig {
  id: string; // ID univoco dell'istanza del child component
  type: string; // Tipo di child component, es. 'CartesianGrid', 'XAxis'
  props: { [key: string]: any }; // Proprietà specifiche del child component
}

export interface ChartConfig {
  id: string; // Identificatore univoco del grafico istanziato
  name: string; // Nome dato dall'utente a questa istanza di grafico
  chartComponentType: string | null; // Es: 'LineChart', 'BarChart' (da chartDefinitions.componentName)
  chartContainerProps: ChartContainerProps; // Proprietà del contenitore del grafico
  childComponents?: ChartChildComponentConfig[]; // Array di child components configurati
  layerId: string | null; // ID del layer da cui provengono i dati (se applicabile)
}

// Interfaccia per le configurazioni di grafico salvate
export interface SavedChartConfig {
  savedId: string; // ID univoco della configurazione salvata
  name: string; // Nome dato dall'utente alla configurazione salvata
  timestamp: number; // Timestamp del salvataggio
  config: ChartConfig; // La configurazione effettiva del grafico
}

export interface ChartSlice {
  chartConfigs: ChartConfig[];
  activeChartConfig: ChartConfig | null;
  savedChartConfigurations: SavedChartConfig[]; // Array per le configurazioni salvate

  addChartConfig: (config: ChartConfig) => void;
  removeChartConfig: (chartId: string) => void;
  setActiveChartConfig: (chartId: string | null) => void;
  updateChartConfig: (chartId: string, updates: Partial<ChartConfig>) => void;

  // Azioni per i Child Components (relative all'activeChartConfig)
  addChildComponentToActiveChart: (childComponentType: string, initialProps?: { [key: string]: any }) => void;
  removeChildComponentFromActiveChart: (childComponentId: string) => void;
  updateChildComponentPropsInActiveChart: (childComponentId: string, propsUpdates: Partial<any>) => void;

  // Azioni per il salvataggio e caricamento delle configurazioni
  saveActiveChartConfiguration: (name: string) => void;
  loadChartConfiguration: (savedId: string) => void;
  deleteSavedChartConfiguration: (savedId: string) => void;
}

// Se lo integri in useGeoData, il tipo del primo argomento di StateCreator cambierà
// export const createChartSlice: StateCreator<GeoDataState & ChartSlice, [], [], ChartSlice> = (set, get) => ({
export const createChartSlice: StateCreator<ChartSlice, [], [], ChartSlice> = (set, get) => ({
  chartConfigs: [],
  activeChartConfig: null,
  savedChartConfigurations: [],
  addChartConfig: (config) =>
    set((state) => ({
      chartConfigs: [...state.chartConfigs, config],
    })),
  removeChartConfig: (chartId) =>
    set((state) => {
      const newChartConfigs = state.chartConfigs.filter((c) => c.id !== chartId);
      let newActiveChartConfig = state.activeChartConfig;
      if (state.activeChartConfig?.id === chartId) {
        newActiveChartConfig = null;
      }
      return {
        chartConfigs: newChartConfigs,
        activeChartConfig: newActiveChartConfig,
      };
    }),
  setActiveChartConfig: (chartId) =>
    set((state) => {
      if (chartId === null) {
        return { activeChartConfig: null };
      }
      const foundConfig = state.chartConfigs.find((c) => c.id === chartId);
      return { activeChartConfig: foundConfig || null };
    }),
  updateChartConfig: (chartId, updates) =>
    set((state) => {
      const newChartConfigs = state.chartConfigs.map((config) => {
        if (config.id === chartId) {
          let updatedConfig = { ...config, ...updates };
          // Gestione specifica per chartContainerProps per permettere aggiornamenti parziali
          if (updates.chartContainerProps) {
            updatedConfig.chartContainerProps = {
              ...(config.chartContainerProps || {}), // Prendi le props esistenti o un oggetto vuoto
              ...updates.chartContainerProps,     // Applica gli aggiornamenti parziali
            };
          }
          // Se margin è aggiornato dentro chartContainerProps, gestisci la fusione
          if (updates.chartContainerProps?.margin) {
            updatedConfig.chartContainerProps.margin = {
              ...(config.chartContainerProps?.margin || {}),
              ...updates.chartContainerProps.margin,
            };
          }

          // Logica per aggiungere automaticamente Area o Scatter child
          if (updates.chartComponentType) {
            const componentType = updates.chartComponentType;
            const existingChildren = updatedConfig.childComponents || [];
            let childToAdd: ChartChildComponentConfig | null = null;

            if (componentType === 'AreaChart' && !existingChildren.some(child => child.type === 'Area')) {
              childToAdd = {
                id: `Area-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
                type: 'Area',
                props: { dataKey: '', fill: '#8884d8', stroke: '#8884d8' }, // Props di default
              };
            } else if (componentType === 'ScatterChart' && !existingChildren.some(child => child.type === 'Scatter')) {
              childToAdd = {
                id: `Scatter-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
                type: 'Scatter',
                props: { dataKey: '', fill: '#8884d8' }, // Props di default
              };
            }

            if (childToAdd) {
              updatedConfig = {
                ...updatedConfig,
                childComponents: [...existingChildren, childToAdd],
              };
            }
          }
          return updatedConfig;
        }
        return config;
      });

      let newActiveChartConfig = state.activeChartConfig;
      if (state.activeChartConfig?.id === chartId) {
        // Troviamo la configurazione aggiornata da newChartConfigs per riflettere
        // l'eventuale aggiunta automatica del child component.
        const potentiallyUpdatedConfigFromArray = newChartConfigs.find(c => c.id === chartId);
        if (potentiallyUpdatedConfigFromArray) {
            newActiveChartConfig = { ...potentiallyUpdatedConfigFromArray };
        } else {
            // Fallback nel caso (improbabile) non sia trovato, applica solo gli updates diretti
            newActiveChartConfig = { ...state.activeChartConfig, ...updates };
             // Gestione specifica per chartContainerProps in activeChartConfig
            if (updates.chartContainerProps && newActiveChartConfig) {
                newActiveChartConfig.chartContainerProps = {
                ...(state.activeChartConfig.chartContainerProps || {}),
                ...updates.chartContainerProps,
                };
                // Se margin è aggiornato dentro chartContainerProps, gestisci la fusione
                if (updates.chartContainerProps.margin && newActiveChartConfig.chartContainerProps) {
                newActiveChartConfig.chartContainerProps.margin = {
                    ...(state.activeChartConfig.chartContainerProps?.margin || {}),
                    ...updates.chartContainerProps.margin,
                };
                }
            }
        }
      }

      return {
        chartConfigs: newChartConfigs,
        activeChartConfig: newActiveChartConfig,
      };
    }),

  // Implementazioni per i Child Components
  addChildComponentToActiveChart: (childComponentType, initialProps = {}) =>
    set((state) => {
      if (!state.activeChartConfig) return state;

      // Genera un ID univoco per il nuovo child component
      const newChildId = `${childComponentType}-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;

      const newChildComponent: ChartChildComponentConfig = {
        id: newChildId,
        type: childComponentType,
        props: initialProps,
      };

      const updatedActiveChartConfig: ChartConfig = {
        ...state.activeChartConfig,
        childComponents: [
          ...(state.activeChartConfig.childComponents || []),
          newChildComponent,
        ],
      };

      const newChartConfigs = state.chartConfigs.map((config) =>
        config.id === updatedActiveChartConfig.id ? updatedActiveChartConfig : config
      );

      return {
        activeChartConfig: updatedActiveChartConfig,
        chartConfigs: newChartConfigs,
      };
    }),

  removeChildComponentFromActiveChart: (childComponentId) =>
    set((state) => {
      if (!state.activeChartConfig || !state.activeChartConfig.childComponents) return state;

      const updatedActiveChartConfig: ChartConfig = {
        ...state.activeChartConfig,
        childComponents: state.activeChartConfig.childComponents.filter(
          (child) => child.id !== childComponentId
        ),
      };

      const newChartConfigs = state.chartConfigs.map((config) =>
        config.id === updatedActiveChartConfig.id ? updatedActiveChartConfig : config
      );

      return {
        activeChartConfig: updatedActiveChartConfig,
        chartConfigs: newChartConfigs,
      };
    }),

  updateChildComponentPropsInActiveChart: (childComponentId, propsUpdates) =>
    set((state) => {
      if (!state.activeChartConfig || !state.activeChartConfig.childComponents) return state;

      let oldAxisId: string | undefined = undefined;
      let newAxisIdFromUpdate: string | undefined = undefined;
      let axisTypeToUpdate: 'XAxis' | 'YAxis' | undefined = undefined;

      // Trova il componente target e determina se è un asse il cui ID sta cambiando
      const targetChildComponent = state.activeChartConfig.childComponents.find(
        (child) => child.id === childComponentId
      );

      if (
        targetChildComponent &&
        (targetChildComponent.type === 'XAxis' || targetChildComponent.type === 'YAxis') &&
        'id' in propsUpdates &&
        typeof propsUpdates.id === 'string' &&
        targetChildComponent.props.id !== propsUpdates.id
      ) {
        oldAxisId = targetChildComponent.props.id; // Questo è l'ID attuale nelle props del componente prima dell'aggiornamento
        newAxisIdFromUpdate = propsUpdates.id;
        axisTypeToUpdate = targetChildComponent.type as 'XAxis' | 'YAxis';
      }

      const updatedChildComponents = state.activeChartConfig.childComponents.map((child) => {
        let newProps = { ...child.props };
        let hasChanged = false;

        // Applica l'aggiornamento diretto delle props al componente target
        if (child.id === childComponentId) {
          newProps = { ...newProps, ...propsUpdates };
          hasChanged = true;
        }

        // Se un ID dell'asse è stato modificato, aggiorna i riferimenti negli altri componenti
        if (axisTypeToUpdate && oldAxisId && newAxisIdFromUpdate) {
          if (axisTypeToUpdate === 'XAxis' && child.props.xAxisId === oldAxisId) {
            newProps.xAxisId = newAxisIdFromUpdate;
            hasChanged = true;
          } else if (axisTypeToUpdate === 'YAxis' && child.props.yAxisId === oldAxisId) {
            newProps.yAxisId = newAxisIdFromUpdate;
            hasChanged = true;
          }
        }
        return hasChanged ? { ...child, props: newProps } : child;
      });
      
      // Controlla se ci sono stati cambiamenti effettivi per evitare un aggiornamento di stato superfluo
      // Questo è un controllo semplice; per strutture complesse potrebbe essere necessario un deep-compare
      const effectivelyChanged = state.activeChartConfig.childComponents.some((origChild, index) => {
        return JSON.stringify(origChild) !== JSON.stringify(updatedChildComponents[index]);
      });

      if (!effectivelyChanged) {
        return state; // Nessun cambiamento effettivo, restituisci lo stato corrente
      }

      const updatedActiveChartConfig: ChartConfig = {
        ...state.activeChartConfig,
        childComponents: updatedChildComponents,
      };

      const newChartConfigs = state.chartConfigs.map((config) =>
        config.id === updatedActiveChartConfig.id ? updatedActiveChartConfig : config
      );

      return {
        activeChartConfig: updatedActiveChartConfig,
        chartConfigs: newChartConfigs,
      };
    }),

  // Implementazione delle azioni di salvataggio/caricamento
  saveActiveChartConfiguration: (name) =>
    set((state) => {
      const activeConfig = get().activeChartConfig;
      if (!activeConfig) {
        console.warn('Nessuna configurazione attiva da salvare.');
        return state;
      }
      const newSavedConfig: SavedChartConfig = {
        savedId: `saved-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        name,
        timestamp: Date.now(),
        config: { ...activeConfig }, // Salva una copia della configurazione attiva
      };
      return {
        savedChartConfigurations: [...state.savedChartConfigurations, newSavedConfig],
      };
    }),

  loadChartConfiguration: (savedId) =>
    set((state) => {
      const savedConfig = state.savedChartConfigurations.find(sc => sc.savedId === savedId);
      if (!savedConfig) {
        console.warn(`Configurazione salvata con ID ${savedId} non trovata.`);
        return state;
      }
      // Quando si carica, la configurazione salvata diventa la nuova activeChartConfig.
      // Potrebbe essere necessario aggiungerla anche a chartConfigs se non si vuole
      // che activeChartConfig sia un'entità "fluttuante" non presente in chartConfigs.
      // Per ora, la impostiamo solo come attiva. L'utente potrebbe volerla "istanziar"e
      // ulteriormente o modificarla e poi salvarla come nuova istanza in chartConfigs.
      // Per coerenza, generiamo un nuovo ID per la ChartConfig attiva se necessario,
      // o manteniamo quello originale se la logica lo prevede.
      // Qui, creiamo una nuova istanza basata su quella salvata, con un nuovo ID.
      const newActiveInstanceId = `chart-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      const newActiveChart: ChartConfig = {
        ...savedConfig.config,
        id: newActiveInstanceId, // Assegna un nuovo ID all'istanza attiva
        name: `${savedConfig.name} (Copia)`, // Indica che è una copia caricata
      };

      // Aggiungi la nuova istanza a chartConfigs e impostala come attiva
      const newChartConfigs = [...state.chartConfigs.filter(c => c.id !== newActiveInstanceId), newActiveChart];

      return {
        activeChartConfig: newActiveChart,
        chartConfigs: newChartConfigs,
      };
    }),

  deleteSavedChartConfiguration: (savedId) =>
    set((state) => ({
      savedChartConfigurations: state.savedChartConfigurations.filter(
        (sc) => sc.savedId !== savedId
      ),
    })),
});